/**
 * Call Saver Queue
 * Manage negative call recovery workflow with AI-powered save plans
 * 
 * Features:
 * - Real-time queue with SLA tracking
 * - AI save plans with callback scripts
 * - Claim/snooze/status management
 * - Team collaboration with notes
 * - Email escalation tracking
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { 
  AlertTriangle, Clock, CheckCircle, XCircle, Pause, 
  Filter, Download, Search, Phone, User, Calendar,
  ChevronRight, TrendingUp, Send, MessageSquare, X,
  PlayCircle, RotateCcw, AlertCircleIcon
} from 'lucide-react';
import { CallRecordingPlayer } from '@/components/agent-console/CallRecordingPlayer';
import { TranscriptViewer } from '@/components/agent-console/TranscriptViewer';

interface QueueEntry {
  id: string;
  call_id: string;
  status: 'open' | 'in_progress' | 'saved' | 'not_recoverable' | 'snoozed';
  brand: string;
  negative_reason_codes: string[];
  one_liner: string;
  caller_name: string | null;
  caller_number: string;
  call_started_at: string;
  agent_user_id: string | null;
  owner_id: string | null;
  owner_name: string | null;
  saveability_score: number;
  ai_save_plan: any;
  created_at: string;
  acked_at: string | null;
  resolved_at: string | null;
  snoozed_until: string | null;
  escalation_count: number;
  last_activity_at: string;
  // From view with SLA calculations
  time_to_ack_minutes: number | null;
  time_to_resolve_minutes: number | null;
  tta_breached: boolean;
  ttr_breached: boolean;
  // Joined data
  call?: any;
  notes?: any[];
  emails?: any[];
}

export default function CallSaverQueue() {
  const { user } = useAuth();
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [filteredQueue, setFilteredQueue] = useState<QueueEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<QueueEntry | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string[]>(['open', 'in_progress']);
  const [brandFilter, setBrandFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [slaFilter, setSlaFilter] = useState('all'); // 'all', 'breached', 'at_risk'
  const [sortBy, setSortBy] = useState('saveability'); // 'saveability', 'created_at', 'tta'
  const [searchQuery, setSearchQuery] = useState('');

  // Metrics
  const [metrics, setMetrics] = useState({
    totalOpen: 0,
    ttaBreached: 0,
    ttrBreached: 0,
    avgSaveability: 0,
    savedToday: 0
  });

  // Note form
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'note' | 'callback_scheduled'>('note');

  useEffect(() => {
    loadQueue();
    
    // Set up realtime subscription
    const subscription = supabase
      .channel('call_saver_queue_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'call_saver_queue'
        },
        () => {
          loadQueue();
        }
      )
      .subscribe();

    // Refresh every 30 seconds
    const interval = setInterval(loadQueue, 30000);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    filterQueue();
  }, [queue, statusFilter, brandFilter, ownerFilter, slaFilter, sortBy, searchQuery]);

  const loadQueue = async () => {
    setLoading(true);
    try {
      // Load from view with SLA calculations
      const { data: queueData, error } = await supabase
        .from('call_saver_queue_with_slas')
        .select(`
          *,
          calls (
            *,
            call_recordings (*),
            call_transcripts (*),
            call_outcomes (*)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Load notes for each entry
      const entriesWithNotes = await Promise.all(
        (queueData || []).map(async (entry: any) => {
          const { data: notes } = await supabase
            .from('call_notes')
            .select('*')
            .eq('call_id', entry.call_id)
            .order('created_at', { ascending: false });

          const { data: emails } = await supabase
            .from('email_log')
            .select('*')
            .eq('call_id', entry.call_id)
            .order('sent_at', { ascending: false });

          // Get owner name if owner_id exists
          let owner_name = null;
          if (entry.owner_id) {
            const { data: owner } = await supabase
              .from('users')
              .select('first_name, last_name')
              .eq('id', entry.owner_id)
              .single();
            
            if (owner) {
              owner_name = `${owner.first_name} ${owner.last_name}`;
            }
          }

          return {
            ...entry,
            call: entry.calls?.[0],
            notes: notes || [],
            emails: emails || [],
            owner_name
          };
        })
      );

      setQueue(entriesWithNotes as QueueEntry[]);
      calculateMetrics(entriesWithNotes as QueueEntry[]);
    } catch (error) {
      console.error('Error loading queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterQueue = () => {
    let filtered = [...queue];

    // Status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter(e => statusFilter.includes(e.status));
    }

    // Brand filter
    if (brandFilter !== 'all') {
      filtered = filtered.filter(e => e.brand === brandFilter);
    }

    // Owner filter
    if (ownerFilter !== 'all') {
      if (ownerFilter === 'mine') {
        filtered = filtered.filter(e => e.owner_id === user?.id);
      } else if (ownerFilter === 'unassigned') {
        filtered = filtered.filter(e => !e.owner_id);
      }
    }

    // SLA filter
    if (slaFilter === 'breached') {
      filtered = filtered.filter(e => e.tta_breached || e.ttr_breached);
    } else if (slaFilter === 'at_risk') {
      filtered = filtered.filter(e => {
        const minutesOpen = e.time_to_ack_minutes || 0;
        return !e.acked_at && minutesOpen >= 3 && minutesOpen < 5;
      });
    }

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        e.caller_number.includes(query) ||
        e.caller_name?.toLowerCase().includes(query) ||
        e.one_liner.toLowerCase().includes(query) ||
        e.brand.toLowerCase().includes(query)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'saveability':
          return b.saveability_score - a.saveability_score;
        case 'created_at':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'tta':
          return (b.time_to_ack_minutes || 0) - (a.time_to_ack_minutes || 0);
        default:
          return 0;
      }
    });

    setFilteredQueue(filtered);
  };

  const calculateMetrics = (queueList: QueueEntry[]) => {
    const open = queueList.filter(e => e.status === 'open' || e.status === 'in_progress').length;
    const ttaBreach = queueList.filter(e => e.tta_breached).length;
    const ttrBreach = queueList.filter(e => e.ttr_breached).length;
    
    const avgSave = queueList.length > 0
      ? queueList.reduce((sum, e) => sum + e.saveability_score, 0) / queueList.length
      : 0;

    const today = new Date().toDateString();
    const savedToday = queueList.filter(e => 
      e.status === 'saved' && 
      new Date(e.resolved_at || '').toDateString() === today
    ).length;

    setMetrics({
      totalOpen: open,
      ttaBreached: ttaBreach,
      ttrBreached: ttrBreach,
      avgSaveability: Math.round(avgSave),
      savedToday
    });
  };

  const handleClaim = async (entryId: string) => {
    try {
      const { error } = await supabase
        .from('call_saver_queue')
        .update({
          owner_id: user?.id,
          status: 'in_progress',
          acked_at: new Date().toISOString()
        })
        .eq('id', entryId);

      if (error) throw error;

      // Add system note
      const entry = queue.find(e => e.id === entryId);
      if (entry) {
        await supabase
          .from('call_notes')
          .insert({
            call_id: entry.call_id,
            body: `Call claimed by ${user?.user_metadata?.first_name || 'user'}`,
            type: 'system',
            user_id: user?.id
          });
      }

      loadQueue();
    } catch (error) {
      console.error('Error claiming call:', error);
      alert('Failed to claim call');
    }
  };

  const handleSnooze = async (entryId: string, hours: number) => {
    try {
      const snoozeUntil = new Date();
      snoozeUntil.setHours(snoozeUntil.getHours() + hours);

      const { error } = await supabase
        .from('call_saver_queue')
        .update({
          status: 'snoozed',
          snoozed_until: snoozeUntil.toISOString()
        })
        .eq('id', entryId);

      if (error) throw error;

      // Add system note
      const entry = queue.find(e => e.id === entryId);
      if (entry) {
        await supabase
          .from('call_notes')
          .insert({
            call_id: entry.call_id,
            body: `Call snoozed for ${hours} hours until ${snoozeUntil.toLocaleString()}`,
            type: 'system',
            user_id: user?.id
          });
      }

      loadQueue();
      setSelectedEntry(null);
    } catch (error) {
      console.error('Error snoozing call:', error);
      alert('Failed to snooze call');
    }
  };

  const handleStatusChange = async (entryId: string, newStatus: string) => {
    try {
      const updates: any = { status: newStatus };
      
      if (newStatus === 'saved' || newStatus === 'not_recoverable') {
        updates.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('call_saver_queue')
        .update(updates)
        .eq('id', entryId);

      if (error) throw error;

      // Add system note
      const entry = queue.find(e => e.id === entryId);
      if (entry) {
        await supabase
          .from('call_notes')
          .insert({
            call_id: entry.call_id,
            body: `Status changed to: ${newStatus}`,
            type: 'system',
            user_id: user?.id
          });
      }

      loadQueue();
      if (selectedEntry?.id === entryId && (newStatus === 'saved' || newStatus === 'not_recoverable')) {
        setSelectedEntry(null);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleAddNote = async () => {
    if (!selectedEntry || !newNote.trim()) return;

    try {
      const { error } = await supabase
        .from('call_notes')
        .insert({
          call_id: selectedEntry.call_id,
          body: newNote,
          type: noteType,
          user_id: user?.id
        });

      if (error) throw error;

      setNewNote('');
      setNoteType('note');
      loadQueue();
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
    }
  };

  const exportToCSV = () => {
    const csv = [
      ['Created', 'Brand', 'Caller', 'Number', 'Saveability', 'Status', 'Owner', 'TTA', 'TTR', 'Escalations'].join(','),
      ...filteredQueue.map(entry => [
        new Date(entry.created_at).toLocaleString(),
        entry.brand,
        entry.caller_name || 'Unknown',
        entry.caller_number,
        entry.saveability_score,
        entry.status,
        entry.owner_name || 'Unassigned',
        entry.time_to_ack_minutes || 'N/A',
        entry.time_to_resolve_minutes || 'N/A',
        entry.escalation_count
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-saver-queue-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800 border-red-300';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'saved': return 'bg-green-100 text-green-800 border-green-300';
      case 'not_recoverable': return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'snoozed': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getSaveabilityColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatTimeAgo = (dateString: string) => {
    const minutes = Math.floor((new Date().getTime() - new Date(dateString).getTime()) / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="🧯 Call Saver Queue"
      subtitle="Recover negative calls with AI-powered strategies"
      actions={
        <div className="flex gap-2">
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={loadQueue}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      }
    >
      {/* Metrics */}
      <StatsGrid cols={5}>
        <StatCard
          label="Open Calls"
          value={metrics.totalOpen}
          icon={AlertTriangle}
          variant="warning"
        />
        <StatCard
          label="TTA Breached"
          value={metrics.ttaBreached}
          icon={Clock}
          variant={metrics.ttaBreached > 0 ? 'critical' : 'default'}
        />
        <StatCard
          label="TTR Breached"
          value={metrics.ttrBreached}
          icon={AlertCircleIcon}
          variant={metrics.ttrBreached > 0 ? 'critical' : 'default'}
        />
        <StatCard
          label="Avg Saveability"
          value={`${metrics.avgSaveability}%`}
          icon={TrendingUp}
          variant={metrics.avgSaveability >= 70 ? 'success' : 'default'}
        />
        <StatCard
          label="Saved Today"
          value={metrics.savedToday}
          icon={CheckCircle}
          variant="success"
        />
      </StatsGrid>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Status:</span>
            <div className="flex gap-1">
              {[
                { value: 'open', label: 'Open', color: 'red' },
                { value: 'in_progress', label: 'In Progress', color: 'blue' },
                { value: 'saved', label: 'Saved', color: 'green' },
                { value: 'snoozed', label: 'Snoozed', color: 'yellow' }
              ].map(status => (
                <button
                  key={status.value}
                  onClick={() => {
                    if (statusFilter.includes(status.value)) {
                      setStatusFilter(statusFilter.filter(s => s !== status.value));
                    } else {
                      setStatusFilter([...statusFilter, status.value]);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    statusFilter.includes(status.value)
                      ? `bg-${status.color}-600 text-white border-${status.color}-600`
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>

          {/* Owner Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Owner:</span>
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white text-sm"
            >
              <option value="all">All</option>
              <option value="mine">My Calls</option>
              <option value="unassigned">Unassigned</option>
            </select>
          </div>

          {/* SLA Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">SLA:</span>
            <select
              value={slaFilter}
              onChange={(e) => setSlaFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white text-sm"
            >
              <option value="all">All</option>
              <option value="breached">Breached</option>
              <option value="at_risk">At Risk</option>
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white text-sm"
            >
              <option value="saveability">Saveability</option>
              <option value="created_at">Most Recent</option>
              <option value="tta">Time to Ack</option>
            </select>
          </div>

          {/* Search */}
          <div className="relative ml-auto">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search calls..."
              className="pl-10 pr-4 py-2 border rounded-lg bg-white text-sm w-64"
            />
          </div>
        </div>
      </Card>

      {/* Queue List */}
      <div className="grid grid-cols-3 gap-6">
        {/* Call List */}
        <div className="col-span-2 space-y-3 max-h-[800px] overflow-y-auto">
          {filteredQueue.map((entry) => {
            const isSelected = selectedEntry?.id === entry.id;
            
            return (
              <Card
                key={entry.id}
                className={`p-4 cursor-pointer transition-all ${
                  isSelected
                    ? 'ring-2 ring-blue-500 bg-blue-50'
                    : 'hover:bg-gray-50'
                } ${entry.tta_breached || entry.ttr_breached ? 'border-l-4 border-l-red-500' : ''}`}
                onClick={() => setSelectedEntry(entry)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${getStatusColor(entry.status)}`}>
                        {entry.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {entry.caller_number}
                      </span>
                      {entry.caller_name && (
                        <span className="text-sm text-gray-600">{entry.caller_name}</span>
                      )}
                    </div>

                    <p className="text-sm text-gray-700 mb-2 line-clamp-2">{entry.one_liner}</p>

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{entry.brand}</span>
                      <span>{formatTimeAgo(entry.created_at)}</span>
                      {entry.owner_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {entry.owner_name}
                        </span>
                      )}
                      {entry.escalation_count > 0 && (
                        <span className="text-red-600 font-semibold">
                          🔔 {entry.escalation_count} escalations
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Saveability</div>
                      <div className={`text-2xl font-bold ${getSaveabilityColor(entry.saveability_score)}`}>
                        {entry.saveability_score}%
                      </div>
                    </div>
                    {!entry.owner_id && entry.status === 'open' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClaim(entry.id);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                      >
                        Claim
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}

          {filteredQueue.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3" />
              <p>No calls in queue</p>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="col-span-1">
          {selectedEntry ? (
            <Card className="p-6 sticky top-4">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between pb-4 border-b">
                  <div>
                    <h3 className="font-semibold text-lg">{selectedEntry.caller_number}</h3>
                    <p className="text-sm text-gray-500">{selectedEntry.brand}</p>
                  </div>
                  <button
                    onClick={() => setSelectedEntry(null)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {selectedEntry.status === 'in_progress' && (
                    <>
                      <button
                        onClick={() => handleStatusChange(selectedEntry.id, 'saved')}
                        className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Mark Saved
                      </button>
                      <button
                        onClick={() => handleStatusChange(selectedEntry.id, 'not_recoverable')}
                        className="flex-1 px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                      >
                        <XCircle className="w-4 h-4 inline mr-1" />
                        Not Recoverable
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleSnooze(selectedEntry.id, 2)}
                    className="flex-1 px-3 py-2 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                  >
                    <Pause className="w-4 h-4 inline mr-1" />
                    Snooze 2h
                  </button>
                </div>

                {/* AI Save Plan */}
                {selectedEntry.ai_save_plan && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">AI Save Plan</h4>
                    
                    {selectedEntry.ai_save_plan.callback_scripts && (
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-2">Callback Scripts:</p>
                        <div className="space-y-2">
                          {selectedEntry.ai_save_plan.callback_scripts.map((script: string, i: number) => (
                            <div key={i} className="text-xs bg-gray-50 p-2 rounded border">
                              {script}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedEntry.ai_save_plan.recovery_moves && (
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-2">Recovery Moves:</p>
                        <ul className="text-xs space-y-1">
                          {selectedEntry.ai_save_plan.recovery_moves.map((move: string, i: number) => (
                            <li key={i} className="flex gap-2">
                              <span>•</span>
                              <span>{move}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div>
                  <h4 className="font-semibold text-sm mb-2">Notes ({selectedEntry.notes?.length || 0})</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto mb-2">
                    {selectedEntry.notes?.map((note: any) => (
                      <div key={note.id} className="text-xs bg-gray-50 p-2 rounded">
                        <p className="text-gray-700">{note.body}</p>
                        <p className="text-gray-400 text-xs mt-1">
                          {new Date(note.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="space-y-2">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note..."
                      className="w-full px-3 py-2 border rounded text-sm"
                      rows={3}
                    />
                    <button
                      onClick={handleAddNote}
                      disabled={!newNote.trim()}
                      className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-300"
                    >
                      <Send className="w-4 h-4 inline mr-1" />
                      Add Note
                    </button>
                  </div>
                </div>

                {/* Call Recording & Transcript */}
                {selectedEntry.call?.call_recordings?.[0] && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Recording</h4>
                    <CallRecordingPlayer
                      recordingUrl={selectedEntry.call.call_recordings[0].recording_url}
                      duration={selectedEntry.call.call_recordings[0].recording_duration_seconds}
                      callSid={selectedEntry.call.call_sid}
                    />
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <Card className="p-12 text-center text-gray-400">
              <Phone className="w-12 h-12 mx-auto mb-3" />
              <p>Select a call to view details</p>
            </Card>
          )}
        </div>
      </div>
    </AdminPageLayout>
  );
}
